// Customer tracking page (roadmap 2026-Q4, F1: «¿en qué va mi auto?»): a
// public page without an account at app.tallerza.cl/s/<token> with the bare
// minimum about one repair order (first name, car, board phase, estimated
// delivery, «listo para retiro» and the Ingreso PDF link). Rendered by the
// Function `seguimiento` (firebase/functions/src/seguimiento.ts); the token
// reuses the own-link machinery of the PDF (shared/src/enlaces.ts): 128
// random bits stored as SHA-256 in the root collection `enlacesPdf`, with
// `tipo: 'orden'`. Behind the same `enlacesPdf` switch of the workshop.
import { z } from 'zod';
import { TOKEN_ENLACE_PDF } from './enlaces.ts';

const DIA_MS = 24 * 60 * 60 * 1000;

/** Owner's decision D4: valid until the order is delivered + 7 days. */
export const GRACIA_SEGUIMIENTO_DIAS = 7;
export const GRACIA_SEGUIMIENTO_MS = GRACIA_SEGUIMIENTO_DIAS * DIA_MS;
/**
 * Hard ceiling of a token (its `expiraEn`, which the TTL policy of
 * `enlacesPdf` uses to delete it): an order that is never closed must not
 * keep a public page forever. The page itself also checks delivery + 7.
 */
export const TOPE_SEGUIMIENTO_DIAS = 180;
export const TOPE_SEGUIMIENTO_MS = TOPE_SEGUIMIENTO_DIAS * DIA_MS;

/** Value of `tipo` in an `enlacesPdf` document of a tracking link (absent = PDF). */
export const TIPO_ENLACE_SEGUIMIENTO = 'orden';

/** Public base of the links (Hosting rewrite /s/** of the `tallerza` site). */
export const BASE_SEGUIMIENTO = 'https://app.tallerza.cl/s/';
export const urlSeguimiento = (token: string) => `${BASE_SEGUIMIENTO}${token}`;

/** Token of a request path: exactly `/s/<token>`; anything else is null (404). */
export function tokenDeRutaSeguimiento(ruta: string | undefined): string | null {
  const t = /^\/s\/([^/]+)$/.exec(ruta ?? '')?.[1];
  return t && TOKEN_ENLACE_PDF.test(t) ? t : null;
}

/**
 * Whether an order's tracking page is still valid by D4. An open order is;
 * a closed one only until `cerradaEn` + 7 days. A closed order without a
 * readable closing time (NaN) fails closed.
 */
export function seguimientoVigente(
  o: { cerrada: boolean; cerradaEnMs: number },
  ahora: number,
): boolean {
  if (!o.cerrada) return true;
  return ahora < o.cerradaEnMs + GRACIA_SEGUIMIENTO_MS;
}

/**
 * The link as the server exposes it on the order document (field
 * `seguimiento`), so the backoffice can paste it in a notice without issuing
 * a new one each time. Server-written only (the order rules let the client
 * change only `repuestos` and `retencion`). Timestamps written by the server.
 */
export type SeguimientoOrden = {
  url: string;
  creadoEn: unknown;
  revocadoEn?: unknown;
};

const idDoc = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

/**
 * Callables of the workshop's admin or jefe de taller:
 * - emitirSeguimiento → `{ url, nuevo }`: the order's current link if it is
 *   still valid (nuevo false), or a new one (nuevo true). Needs the
 *   `enlacesPdf` switch on and an order not delivered more than 7 days ago.
 * - revocarSeguimiento → `{ revocado }`: revokes the current link (also while
 *   the workshop is suspended: it only protects). To rotate, revoke and emit.
 */
export const esquemaSeguimientoOrden = z.object({ ordenId: idDoc });
export type SolicitudSeguimiento = z.infer<typeof esquemaSeguimientoOrden>;
export type RespuestaEmitirSeguimiento = { url: string; nuevo: boolean };
export type RespuestaRevocarSeguimiento = { revocado: boolean };

/** First name only (the page never shows the full name). */
export function nombreDePila(nombre: unknown): string {
  if (typeof nombre !== 'string') return '';
  return (nombre.trim().split(/\s+/)[0] ?? '').slice(0, 40);
}

/** At most the last two characters of the plate (never the full plate). */
export function patenteParcial(patente: unknown): string {
  if (typeof patente !== 'string') return '';
  const limpia = patente.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return limpia.length >= 4 ? limpia.slice(-2) : '';
}

/**
 * Days left for the estimated delivery: the durations of the remaining
 * phases from the current one (the target one while in the neutral zone),
 * the same rule as the backoffice board (apps/backoffice/src/tablero/logica.ts).
 */
export function diasEstimadosEntrega(
  o: { faseIdx: number; enZonaNeutral: boolean; faseDestino: number | null },
  fases: readonly { duracionDias: number }[],
): number {
  const desde = o.enZonaNeutral ? (o.faseDestino ?? o.faseIdx) : o.faseIdx;
  let dias = 0;
  for (let i = Math.max(0, desde); i < fases.length; i++) dias += fases[i]?.duracionDias ?? 0;
  return dias;
}

export const MENSAJES_SEGUIMIENTO = {
  /** 404: unknown or malformed token. Same text for both: no hint. */
  noEncontrado: 'Este enlace no existe. Revisa que esté completo o pide uno nuevo al taller.',
  /** 410: expired, revoked, delivered more than 7 days ago or switched off. */
  noDisponible: 'Este enlace ya no está disponible. Si necesitas algo, contacta al taller.',
  limite: 'Demasiados intentos. Espera un momento y vuelve a intentar.',
  interno: 'No se pudo mostrar el estado de tu auto. Intenta de nuevo en unos minutos.',
} as const;
